/**
 * Chakra UI Compatibility Layer
 * Drop-in replacement for components-lib imports
 * 
 * Usage: Replace imports from 'components-lib' with this file
 * 
 * Before:
 *   import { Box, Flex, Button } from 'components-lib';
 * 
 * After:
 *   import { Box, Flex, Button } from 'components-lib/chakra-compat';
 * 
 * Or configure path alias:
 *   'components-lib': 'src/components-lib/chakra-compat'
 */

import React, { useState, useRef, useContext } from 'react';
import { Box } from './primitives';
import { splitProps } from './system';
import { useOutsideClick } from './hooks';
import { keyframes } from './keyframes';

// Import specific modules to avoid circular dependency with index.js
// (index.js imports from chakra-compat, so chakra-compat can't import from index.js)

// Theme exports
export { ChakraProvider, extendTheme, useTheme, withTheme, defaultTheme, ThemeContext } from './theme';

// System exports  
export { chakra, transformStyleProps, splitProps, stylePropsKeys, useColorMode, useColorModeValue, ColorModeProvider, LightMode, DarkMode, mode } from './system';

// Primitives exports
export { Box, Flex, Grid, GridItem, Stack, VStack, HStack, Text, Heading, Icon, createIcon, Divider, Spacer, Badge, Link, List, ListItem, ListIcon, OrderedList, UnorderedList, Portal } from './primitives';

// Forms exports
export { Button, ButtonGroup, IconButton, Input, Select, Textarea, Switch, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, FormControl, FormLabel, FormErrorMessage, FormHelperText, RequiredIndicator, useFormControlContext, InputGroup, InputLeftElement, InputRightElement, InputLeftAddon, InputRightAddon } from './forms';

// Overlay exports
export { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useModalContext } from './overlay';

// Feedback exports (Toast aliases point to Sonar implementation)
export { Spinner, ToastProvider, useToast } from './feedback';

// Navigation exports
export { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator } from './navigation';

// Data display exports
export { Card, CardHeader, CardBody, CardFooter } from './data-display';

// Layout exports
export { SimpleGrid, Container, Center, Square, Circle } from './layout';

// Hooks exports
export { useDisclosure, useOutsideClick, useStyleConfig, useMultiStyleConfig } from './hooks';

// Additional Chakra UI API compatibility

// Re-export forwardRef from React for convenience
export { forwardRef } from 'react';

// CSS helper (simplified version)
export const css = (styles) => styles;

// Responsive array/object helper (simplified - returns first/base value)
export const responsive = (value) => {
  if (Array.isArray(value)) return value[0];
  if (typeof value === 'object' && value !== null) {
    return value.base || value.sm || Object.values(value)[0];
  }
  return value;
};

export { keyframes } from './keyframes';
export { useBreakpointValue } from './useBreakpointValue';

// visually hidden styles
export const visuallyHiddenStyle = {
  border: 0,
  clip: 'rect(0, 0, 0, 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  width: '1px',
  whiteSpace: 'nowrap',
  wordWrap: 'normal',
};

/**
 * VisuallyHidden Component
 * Hides content visually but keeps it accessible to screen readers
 */
export const VisuallyHidden = ({ children, ...props }) => (
  <Box
    as="span"
    border="0"
    clip="rect(0, 0, 0, 0)"
    height="1px"
    margin="-1px"
    overflow="hidden"
    padding="0"
    position="absolute"
    width="1px"
    whiteSpace="nowrap"
    wordWrap="normal"
    {...props}
  >
    {children}
  </Box>
);

/**
 * VisuallyHiddenInput Component
 * Hidden input for custom form controls
 */
export const VisuallyHiddenInput = (props) => (
  <Box
    as="input"
    border="0"
    clip="rect(0, 0, 0, 0)"
    height="1px"
    margin="-1px"
    overflow="hidden"
    padding="0"
    position="absolute"
    width="1px"
    whiteSpace="nowrap"
    wordWrap="normal"
    {...props}
  />
);

// Skeleton placeholder (simplified)
export const Skeleton = ({ isLoaded, children, ...props }) => {
  if (isLoaded) return children;
  
  const shimmer = keyframes`
    0% { background-position: -200px 0; }
    100% { background-position: calc(200px + 100%) 0; }
  `;
  
  return (
    <Box
      bg="gray.200"
      borderRadius="md"
      backgroundImage="linear-gradient(90deg, #EDF2F7 0px, #E2E8F0 50%, #EDF2F7 100%)"
      backgroundSize="200px 100%"
      animation={`${shimmer} 1.5s infinite linear`}
      {...props}
    >
      {/* Empty or shimmer animation */}
      <Box opacity={0}>{children || '\u00A0'}</Box>
    </Box>
  );
};

// SkeletonText placeholder
export const SkeletonText = ({ noOfLines = 3, spacing = 2, ...props }) => (
  <Box display="flex" flexDirection="column" gap={spacing} {...props}>
    {Array.from({ length: noOfLines }).map((_, i) => (
      <Skeleton
        key={i}
        height="0.75rem"
        width={i === noOfLines - 1 ? '80%' : '100%'}
      />
    ))}
  </Box>
);

// SkeletonCircle placeholder  
export const SkeletonCircle = ({ size = '2rem', ...props }) => (
  <Skeleton
    width={size}
    height={size}
    borderRadius="full"
    {...props}
  />
);

// CloseButton component
export const CloseButton = ({ size = 'md', ...props }) => {
  const sizeMap = {
    sm: { size: '24px', iconSize: '10px' },
    md: { size: '32px', iconSize: '12px' },
    lg: { size: '40px', iconSize: '16px' },
  };
  const { size: btnSize, iconSize } = sizeMap[size] || sizeMap.md;

  return (
    <Box
      as="button"
      type="button"
      aria-label="Close"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      width={btnSize}
      height={btnSize}
      borderRadius="md"
      border="none"
      bg="transparent"
      cursor="pointer"
      p={0}
      transition="background 0.2s"
      _hover={{ bg: 'blackAlpha.100' }}
      {...props}
    >
      <svg
        viewBox="0 0 24 24"
        width={iconSize}
        height={iconSize}
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </Box>
  );
};

// Image component (basic)
export const Image = ({ src, alt, fallbackSrc, fallback, ...props }) => {
  const handleError = (e) => {
    if (fallbackSrc) {
      e.target.src = fallbackSrc;
    }
  };

  return (
    <Box
      as="img"
      src={src}
      alt={alt}
      onError={handleError}
      maxWidth="100%"
      height="auto"
      {...props}
    />
  );
};

// Avatar placeholder (basic)
export const Avatar = ({ name, src, size = 'md', ...props }) => {
  const sizeMap = {
    '2xs': '1rem',
    xs: '1.5rem',
    sm: '2rem',
    md: '3rem',
    lg: '4rem',
    xl: '6rem',
    '2xl': '8rem',
  };
  const avatarSize = sizeMap[size] || size;
  
  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Box
      width={avatarSize}
      height={avatarSize}
      borderRadius="full"
      bg="gray.200"
      display="flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
      fontSize={`calc(${avatarSize} / 2.5)`}
      fontWeight="medium"
      color="gray.600"
      {...props}
    >
      {src ? (
        <Box
          as="img"
          src={src}
          alt={name}
          width="100%"
          height="100%"
          objectFit="cover"
        />
      ) : (
        getInitials(name)
      )}
    </Box>
  );
};

// AvatarGroup placeholder
export const AvatarGroup = ({ max, children, spacing = -3, ...props }) => {
  const avatars = React.Children.toArray(children);
  const excess = max ? avatars.length - max : 0;
  const displayAvatars = max ? avatars.slice(0, max) : avatars;

  return (
    <Box
      display="flex"
      flexDirection="row-reverse"
      {...props}
    >
      {excess > 0 && (
        <Avatar
          name={`+${excess}`}
          ml={spacing}
          zIndex={0}
        />
      )}
      {displayAvatars.reverse().map((avatar, index) =>
        React.cloneElement(avatar, {
          key: index,
          ml: index === 0 ? 0 : spacing,
          zIndex: index + 1,
          border: '2px solid white',
          ...avatar.props
        })
      )}
    </Box>
  );
};

// Tooltip placeholder (basic - just renders children)
export const Tooltip = ({ label, children, ...props }) => {
  return (
    <Box as="span" title={label} {...props}>
      {children}
    </Box>
  );
};

// Menu components
const MenuContext = React.createContext();

export const Menu = ({ children, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef();
  
  useOutsideClick({
    ref: menuRef,
    handler: () => setIsOpen(false),
  });

  return (
    <MenuContext.Provider value={{ isOpen, setIsOpen, menuRef }}>
      <Box position="relative" display="inline-block" ref={menuRef} {...props}>
        {children}
      </Box>
    </MenuContext.Provider>
  );
};

export const MenuButton = ({ children, ...props }) => {
  const { isOpen, setIsOpen } = useContext(MenuContext);
  return (
    <Box
      as="button"
      onClick={() => setIsOpen(!isOpen)}
      aria-expanded={isOpen}
      cursor="pointer"
      {...props}
    >
      {children}
    </Box>
  );
};

export const MenuList = ({ children, ...props }) => {
  const { isOpen } = useContext(MenuContext);
  return (
    <Box
      display={isOpen ? 'block' : 'none'}
      position="absolute"
      zIndex={1000}
      minW="10rem"
      bg="white"
      boxShadow="lg"
      borderRadius="md"
      py={2}
      mt={1}
      right={0}
      border="1px solid"
      borderColor="gray.200"
      {...props}
    >
      {children}
    </Box>
  );
};

export const MenuItem = ({ children, ...props }) => (
  <Box
    as="button"
    display="block"
    w="100%"
    textAlign="left"
    px={4}
    py={2}
    bg="transparent"
    _hover={{ bg: 'gray.100' }}
    cursor="pointer"
    border="none"
    {...props}
  >
    {children}
  </Box>
);

export const MenuDivider = () => <Box as="hr" my={2} borderColor="gray.200" />;
export const MenuGroup = ({ children, title }) => (
  <Box>
    {title && <Box fontWeight="bold" px={4} py={2} fontSize="sm" color="gray.500">{title}</Box>}
    {children}
  </Box>
);

// Popover placeholders
export const Popover = ({ children }) => children;
export const PopoverTrigger = ({ children }) => children;
export const PopoverContent = ({ children }) => <Box>{children}</Box>;
export const PopoverHeader = ({ children }) => <Box>{children}</Box>;
export const PopoverBody = ({ children }) => <Box>{children}</Box>;
export const PopoverFooter = ({ children }) => <Box>{children}</Box>;
export const PopoverCloseButton = CloseButton;

// Drawer placeholders (use Modal as base)
export { Modal as Drawer } from './overlay';
export { ModalOverlay as DrawerOverlay } from './overlay';
export { ModalContent as DrawerContent } from './overlay';
export { ModalHeader as DrawerHeader } from './overlay';
export { ModalBody as DrawerBody } from './overlay';
export { ModalFooter as DrawerFooter } from './overlay';
export { ModalCloseButton as DrawerCloseButton } from './overlay';

// AlertDialog placeholders (use Modal as base)
export { Modal as AlertDialog } from './overlay';
export { ModalOverlay as AlertDialogOverlay } from './overlay';
export { ModalContent as AlertDialogContent } from './overlay';
export { ModalHeader as AlertDialogHeader } from './overlay';
export { ModalBody as AlertDialogBody } from './overlay';
export { ModalFooter as AlertDialogFooter } from './overlay';
export { ModalCloseButton as AlertDialogCloseButton } from './overlay';

// Tabs placeholders
export const Tabs = ({ children, ...props }) => <Box {...props}>{children}</Box>;
export const TabList = ({ children, ...props }) => (
  <Box display="flex" borderBottom="1px solid" borderColor="gray.200" {...props}>
    {children}
  </Box>
);
export const Tab = ({ children, isSelected, ...props }) => (
  <Box
    as="button"
    px={4}
    py={2}
    border="none"
    bg="transparent"
    cursor="pointer"
    borderBottom={isSelected ? '2px solid' : '2px solid transparent'}
    borderColor={isSelected ? 'blue.500' : 'transparent'}
    color={isSelected ? 'blue.500' : 'inherit'}
    {...props}
  >
    {children}
  </Box>
);
export const TabPanels = ({ children }) => <Box>{children}</Box>;
export const TabPanel = ({ children, ...props }) => <Box {...props}>{children}</Box>;

// Table components
export const Table = ({ children, ...props }) => (
  <Box as="table" width="100%" borderCollapse="collapse" {...props}>
    {children}
  </Box>
);
export const Thead = ({ children, ...props }) => <Box as="thead" bg="gray.50" {...props}>{children}</Box>;
export const Tbody = ({ children, ...props }) => <Box as="tbody" {...props}>{children}</Box>;
export const Tfoot = ({ children, ...props }) => <Box as="tfoot" {...props}>{children}</Box>;
export const Tr = ({ children, ...props }) => (
  <Box 
    as="tr" 
    _hover={{ bg: 'gray.50' }} 
    transition="background 0.2s"
    {...props}
  >
    {children}
  </Box>
);
export const Th = ({ children, ...props }) => (
  <Box
    as="th"
    textAlign="left"
    p={4}
    borderBottom="1px solid"
    borderColor="gray.200"
    fontWeight="bold"
    textTransform="uppercase"
    fontSize="xs"
    color="gray.500"
    letterSpacing="wider"
    {...props}
  >
    {children}
  </Box>
);
export const Td = ({ children, ...props }) => (
  <Box
    as="td"
    p={4}
    borderBottom="1px solid"
    borderColor="gray.100"
    {...props}
  >
    {children}
  </Box>
);
export const TableCaption = ({ children, ...props }) => (
  <Box as="caption" p={4} color="gray.500" textAlign="center" {...props}>{children}</Box>
);
export const TableContainer = ({ children, ...props }) => (
  <Box overflowX="auto" borderRadius="lg" border="1px solid" borderColor="gray.200" {...props}>
    {children}
  </Box>
);

// Tag components
export const Tag = ({ children, size = 'md', colorScheme = 'gray', ...props }) => {
  const sizeStyles = {
    sm: { fontSize: 'xs', px: 2, py: 0.5 },
    md: { fontSize: 'sm', px: 2, py: 1 },
    lg: { fontSize: 'md', px: 3, py: 1.5 },
  };
  const { fontSize, px, py } = sizeStyles[size] || sizeStyles.md;

  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      bg="gray.200"
      borderRadius="md"
      fontSize={fontSize}
      px={px}
      py={py}
      fontWeight="medium"
      {...props}
    >
      {children}
    </Box>
  );
};
export const TagLabel = ({ children }) => <Box as="span">{children}</Box>;
export const TagLeftIcon = ({ children }) => (
  <Box as="span" mr={1}>{children}</Box>
);
export const TagRightIcon = ({ children }) => (
  <Box as="span" ml={1}>{children}</Box>
);
export const TagCloseButton = (props) => (
  <Box
    as="button"
    type="button"
    aria-label="close"
    ml={1}
    border="none"
    bg="transparent"
    cursor="pointer"
    p={0}
    display="flex"
    alignItems="center"
    {...props}
  >
    ×
  </Box>
);

// Progress component
export const Progress = ({ value = 0, colorScheme = 'blue', size = 'md', ...props }) => {
  const sizeMap = { xs: '4px', sm: '8px', md: '12px', lg: '16px' };
  const height = sizeMap[size] || sizeMap.md;

  return (
    <Box
      width="100%"
      bg="gray.200"
      borderRadius="full"
      overflow="hidden"
      height={height}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
      {...props}
    >
      <Box
        width={`${Math.min(100, Math.max(0, value))}%`}
        height="100%"
        bg={`${colorScheme}.500`}
        borderRadius="full"
        transition="width 0.3s"
      />
    </Box>
  );
};
export const ProgressLabel = ({ children }) => <Box as="span">{children}</Box>;

// Slider placeholders
export const Slider = ({ children, ...props }) => <Box {...props}>{children}</Box>;
export const SliderTrack = ({ children }) => <Box>{children}</Box>;
export const SliderFilledTrack = () => <Box />;
export const SliderThumb = () => <Box />;
export const SliderMark = ({ children }) => <Box>{children}</Box>;

// Checkbox component (basic)
export const Checkbox = ({ isChecked, isDisabled, children, onChange, ...props }) => {
  const [styleProps, otherProps] = splitProps(props);
  return (
    <Box
      as="label"
      display="inline-flex"
      alignItems="center"
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      opacity={isDisabled ? 0.4 : 1}
      {...styleProps}
    >
      <Box
        as="input"
        type="checkbox"
        checked={isChecked}
        disabled={isDisabled}
        onChange={onChange}
        mr={2}
        {...otherProps}
      />
      {children}
    </Box>
  );
};
export const CheckboxGroup = ({ children, ...props }) => <Box {...props}>{children}</Box>;

// Radio component (basic)
export const Radio = ({ isChecked, isDisabled, children, ...props }) => {
  const [styleProps, otherProps] = splitProps(props);
  return (
    <Box
      as="label"
      display="inline-flex"
      alignItems="center"
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      opacity={isDisabled ? 0.4 : 1}
      {...styleProps}
    >
      <Box
        as="input"
        type="radio"
        checked={isChecked}
        disabled={isDisabled}
        mr={2}
        {...otherProps}
      />
      {children}
    </Box>
  );
};
export const RadioGroup = ({ children, ...props }) => <Box {...props}>{children}</Box>;

// Alert component
export const Alert = ({ status = 'info', children, ...props }) => {
  const statusColors = {
    info: { bg: 'blue.100', border: 'blue.500' },
    warning: { bg: 'orange.100', border: 'orange.500' },
    success: { bg: 'green.100', border: 'green.500' },
    error: { bg: 'red.100', border: 'red.500' },
  };
  const { bg, border } = statusColors[status] || statusColors.info;

  return (
    <Box
      role="alert"
      display="flex"
      alignItems="center"
      p={3}
      bg={bg}
      borderLeftWidth="4px"
      borderLeftColor={border}
      borderRadius="md"
      {...props}
    >
      {children}
    </Box>
  );
};
export const AlertIcon = ({ status }) => {
  const icons = {
    info: 'ℹ',
    warning: '⚠',
    success: '✓',
    error: '✕',
  };
  return <Box as="span" mr={3}>{icons[status] || icons.info}</Box>;
};
export const AlertTitle = ({ children }) => (
  <Box as="span" fontWeight="bold" mr={2}>{children}</Box>
);
export const AlertDescription = ({ children }) => <Box as="span">{children}</Box>;

// Stat components
export const Stat = ({ children, ...props }) => <Box {...props}>{children}</Box>;
export const StatLabel = ({ children }) => (
  <Box fontSize="sm" color="gray.500">{children}</Box>
);
export const StatNumber = ({ children }) => (
  <Box fontSize="2xl" fontWeight="bold">{children}</Box>
);
export const StatHelpText = ({ children }) => (
  <Box fontSize="sm" color="gray.500">{children}</Box>
);
export const StatArrow = ({ type }) => (
  <Box as="span" color={type === 'increase' ? 'green.500' : 'red.500'}>
    {type === 'increase' ? '▲' : '▼'}
  </Box>
);
export const StatGroup = ({ children, ...props }) => (
  <Box display="flex" gap={8} {...props}>
    {children}
  </Box>
);

// Accordion placeholders
export const Accordion = ({ children, ...props }) => <Box {...props}>{children}</Box>;
export const AccordionItem = ({ children }) => <Box>{children}</Box>;
export const AccordionButton = ({ children, ...props }) => (
  <Box as="button" width="100%" textAlign="left" p={4} {...props}>
    {children}
  </Box>
);
export const AccordionPanel = ({ children }) => <Box p={4}>{children}</Box>;
export const AccordionIcon = () => <Box as="span">▼</Box>;

// Wrap component
export const Wrap = ({ children, spacing = 2, ...props }) => (
  <Box
    display="flex"
    flexWrap="wrap"
    gap={spacing}
    {...props}
  >
    {children}
  </Box>
);
export const WrapItem = ({ children, ...props }) => <Box {...props}>{children}</Box>;

// AspectRatio component
export const AspectRatio = ({ ratio = 4 / 3, children, ...props }) => (
  <Box
    position="relative"
    paddingBottom={`${(1 / ratio) * 100}%`}
    {...props}
  >
    <Box
      position="absolute"
      top="0"
      left="0"
      right="0"
      bottom="0"
    >
      {children}
    </Box>
  </Box>
);

// Kbd component
export const Kbd = ({ children, ...props }) => (
  <Box
    as="kbd"
    bg="gray.100"
    borderRadius="md"
    border="1px solid"
    borderColor="gray.300"
    borderBottomWidth="3px"
    fontSize="sm"
    fontFamily="mono"
    px={1.5}
    py={0.5}
    {...props}
  >
    {children}
  </Box>
);

// Code component
export const Code = ({ children, colorScheme, ...props }) => (
  <Box
    as="code"
    bg="gray.100"
    borderRadius="md"
    fontSize="sm"
    fontFamily="mono"
    px={1.5}
    py={0.5}
    {...props}
  >
    {children}
  </Box>
);

// Highlight - just renders children
export const Highlight = ({ children, query, styles }) => <Box as="span">{children}</Box>;
